import React from 'react';
import { FormattedHTMLMessage, injectIntl, InjectedIntl } from 'react-intl';
import { produce } from 'immer';
import { GoogleLoginResponse } from 'react-google-login';
import {
    VerificationStepsEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    Locale,
    MemberPersonalInfoViewModel,
    MemberPersonalInfoResponse,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    getAvailableLocaleChoices,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    ChangeLocaleComponent,
    MarketConsentWrapper,
    CountryComponentWrapper,
} from '@sheerid/jslib';

import { FormFooterComponent } from '../../FormFooterComponent';
import { EmailComponent as Email } from '../../FormFields/EmailComponent';
import { MemberIdComponent as MemberId } from '../../FormFields/MemberIdComponent';
import { addGoogleFiEmailValidator, addMemberIdValidator, overrideStateDepartmentEmailValidator } from '../../../lib/validators/validators';
import { handleOauthOnSuccess } from '../../../lib/utils/stepComponentHelpers';
import { GoogleLoginWrapperComponent } from '../../FormFields/GoogleLoginWrapper';
import { HowDoesVerifyingWorkComponent as HowDoesVerifyingWork } from '../../HowDoesVerifyingWorkComponent';

interface StepMemberPersonalInfoComponentProps {
    intl: InjectedIntl;
    verificationService: VerificationService;
}

const StepMemberPersonalInfo = ({ intl, verificationService }: StepMemberPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MemberPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as MemberPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const localeChoices: FormSelectChoice<Locale, string>[] = getAvailableLocaleChoices(verificationService.programTheme, intl);
    const defaultLocaleChoice: FormSelectChoice<Locale, string> = { value: 'en-US', label: 'English' };

    const updateMemberViewModel = (key: keyof MemberPersonalInfoViewModel, value: any) => {
        const nextState: MemberPersonalInfoViewModel = produce(viewModel, (draft: MemberPersonalInfoViewModel) => {
            (draft[key] as any) = value;
        });

        verificationService.updateViewModel(nextState);
    };

    const updateLocale = (localeChoice: FormSelectChoice<Locale, string>) => {
        const nextState: MemberPersonalInfoViewModel = produce(viewModel, (draft: MemberPersonalInfoViewModel) => {
            draft.localeChoice = localeChoice;
        });
        verificationService.updateLocale(
            nextState,
            verificationService.programTheme,
            verificationService.verificationResponse.segment,
        );
    };

    React.useEffect(() => {
        addGoogleFiEmailValidator();
        addMemberIdValidator();
        overrideStateDepartmentEmailValidator();
    }, []);

    return (
        <div id="sid-step-member-personal-info" className="sid-l-container">


            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Email" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and email address must match what is on record with your school." />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.stateDepartment.title" defaultMessage="Unlock this Members-Only Offer" />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage id="step.personalInfo.stateDepartment.subtitle"
                            defaultMessage="Verify you're a current member of an eligible organization." />
                            &nbsp;
                            <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }

            {
                localeChoices.length > 1 ?
                    <ChangeLocaleComponent
                        options={localeChoices}
                        value={viewModel.localeChoice || defaultLocaleChoice}
                        isErrored={false}
                        onChange={(localeChoice: FormSelectChoice<Locale, string>) =>
                            updateLocale(localeChoice)}
                    />
                : null
            }

            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="organization"
            />

            <FirstName
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastName
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <MemberId
                value={viewModel.memberId}
                isErrored={!!fieldValidationErrors.memberId}
                isRequired={true}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('memberId', newValue);
                        updateFieldValidationErrorsByFieldId('memberId', newValue, verificationService);
                    }
                }
            />

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                label={
                    <FormattedHTMLMessage
                        id="stateDepartmentEmail"
                        defaultMessage="Official State Department email address"
                    />
                }
                explanation={null}
                onChange={
                    (newValue: string) => {
                        updateMemberViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <div className="sid-googleEmail">
                <Email
                    disabled
                    value={viewModel.metadata && viewModel.metadata.googleEmail ? viewModel.metadata.googleEmail : ''}
                    isErrored={!!fieldValidationErrors.googleEmail}
                    fieldId={'googleEmail'}
                    explanation={
                        <FormattedHTMLMessage
                            id="emailExplanation"
                            defaultMessage="Needed to send you your unique code"
                        />
                    }
                    onChange={
                        (newValue: string) => {
                            updateMemberViewModel('metadata', { ...viewModel.metadata, googleEmail: newValue });
                            updateFieldValidationErrorsByFieldId('googleEmail', newValue, verificationService);
                        }
                    }
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
                />

                <div className="sid-googleEmail__not-signed-in sid-l-space-top-sm">
                    <FormattedHTMLMessage
                        id="notYourAccount"
                        defaultMessage="Not your Google Fi account?"
                    />
                    &nbsp;
                    <GoogleLoginWrapperComponent
                        onSuccess={(response) => {
                            const newGoogleEmail = (response as GoogleLoginResponse).getBasicProfile().getEmail();
                            handleOauthOnSuccess(response);
                            updateMemberViewModel('metadata', { ...viewModel.metadata, googleEmail: newGoogleEmail });
                        }}
                        buttonComponent={renderProps => (
                            <div
                                tabIndex={0}
                                className="sid-link sid-h-link-like"
                                onClick={() => {
                                    renderProps.onClick();
                                }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                    if (e.key === 'Enter' || e.key === 'Spacebar' || e.key === ' ') {
                                        renderProps.onClick();
                                    }
                                }}
                                role="link"
                            >
                                <FormattedHTMLMessage
                                    id="switchNow"
                                    defaultMessage="Switch now"
                                />
                            </div>
                        )}
                    />
                </div>
            </div>

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: string) => {
                                updateMemberViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                : null
            }

            <div className="sid-l-space-top-md">
                <FormattedHTMLMessage id="otherMembers" defaultMessage="Other members of your group plan will get the same international exceptions while you're serving abroad and active on Fi." />
            </div>

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectMemberPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyMembershipStatus" defaultMessage="Verify My Membership Status" />
                            }
                        </span>
                    </button>
                </div>
            </div>

            <hr className="sid-l-horz-separator sid-l-space-top-md"></hr>

            <FormFooterComponent verificationService={verificationService} />
        </div>
    );
};

export const StepMemberPersonalInfoComponent = injectIntl(StepMemberPersonalInfo);
